// AI-CONTEXT: See docs/features/supertenant-toc-path-detail.md
// AI-CONTEXT: See docs/features/supertenant-common-toc-paths.md
// De ResponsiveContainer hieronder staat op height='100%'. Dat is geen hoogte
// maar een verwijzing naar de ouder: hangt deze grafiek in een container zonder
// OPGEGEVEN hoogte, dan tekent hij niets. Zonder fout, zonder lege toestand.
// AI-CONTEXT: See docs/pitfalls.md#ui-chart-renders-blank-after-move
import { ResponsiveContainer, Sankey, Tooltip, Layer, Rectangle } from 'recharts'

// Brand palette per column (doelgroep → activiteit → mechanisme → effect).
const COLUMN_COLORS = {
    primaryTargetGroup: '#206c7f',
    activityType: '#47acc3',
    mechanismCategory: '#f38284',
    effect: '#206c7f',
}
const FALLBACK_COLOR = '#47acc3'

// Long taxonomy labels overflow the node gap; clip and lean on the tooltip for
// the full text.
const clip = (text, max = 22) => (text && text.length > max ? `${text.slice(0, max - 1)}…` : text)

// A node is dimmed when its column already has a (different) pick — the user is
// assembling one path and the off-path options fade back.
const isNodeDimmed = (selected, column, rawKey) => {
    const chosen = selected && selected[column]
    return !!chosen && chosen !== rawKey
}
const isNodeChosen = (selected, column, rawKey) => !!selected && selected[column] === rawKey

/**
 * Custom Sankey node: a coloured bar per column, clickable to pin that column.
 *
 * @param {object} props
 * @param {number} props.x
 * @param {number} props.y
 * @param {number} props.width
 * @param {number} props.height
 * @param {{name: string, column: string, rawKey: string, orgs: number}} props.payload
 * @param {string} props.firstColumn
 * @param {string} props.lastColumn
 * @param {Object<string, string>} props.selected - column → chosen rawKey
 * @param {(column: string, rawKey: string) => void} props.onNodeClick
 */
const PathNode = ({ x, y, width, height, payload, firstColumn, lastColumn, selected, onNodeClick }) => {
    const color = COLUMN_COLORS[payload.column] || FALLBACK_COLOR
    const dimmed = isNodeDimmed(selected, payload.column, payload.rawKey)
    const chosen = isNodeChosen(selected, payload.column, payload.rawKey)
    const isFirst = payload.column === firstColumn
    const isLast = payload.column === lastColumn

    let labelX = x + width / 2
    let anchor = 'middle'
    let labelY = y - 5
    let baseline = 'baseline'
    if (isFirst) {
        labelX = x - 6; anchor = 'end'; labelY = y + height / 2; baseline = 'middle'
    } else if (isLast) {
        labelX = x + width + 6; anchor = 'start'; labelY = y + height / 2; baseline = 'middle'
    }

    const handleClick = () => onNodeClick && onNodeClick(payload.column, payload.rawKey)

    return (
        <Layer style={{ cursor: onNodeClick ? 'pointer' : 'default' }} onClick={handleClick}>
            <Rectangle
                x={x}
                y={y}
                width={width}
                height={height}
                fill={color}
                fillOpacity={dimmed ? 0.2 : 0.95}
                stroke={chosen ? '#1a3d47' : 'none'}
                strokeWidth={chosen ? 2 : 0}
                radius={2}
            />
            <text
                className='sector-paths-sankey-label'
                x={labelX}
                y={labelY}
                textAnchor={anchor}
                dominantBaseline={baseline}
                fillOpacity={dimmed ? 0.35 : 1}
                fontWeight={chosen ? 700 : 600}
            >
                {clip(payload.name)}
                <tspan className='sector-paths-sankey-count' dx={5}>{payload.orgs}</tspan>
            </text>
        </Layer>
    )
}

/**
 * Custom Sankey link: dims when either endpoint is a faded (off-path) node.
 */
const PathLink = ({ sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth, payload, selected }) => {
    const src = payload && typeof payload.source === 'object' ? payload.source : null
    const tgt = payload && typeof payload.target === 'object' ? payload.target : null
    const dimmed =
        (src && isNodeDimmed(selected, src.column, src.rawKey)) ||
        (tgt && isNodeDimmed(selected, tgt.column, tgt.rawKey))

    return (
        <path
            d={`M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
            fill='none'
            stroke={FALLBACK_COLOR}
            strokeWidth={Math.max(1, linkWidth)}
            strokeOpacity={dimmed ? 0.06 : 0.3}
        />
    )
}

/**
 * Sankey of the most common TOC paths in a sector: doelgroep → activiteit →
 * mechanisme → effect, flow width = distinct organisations. Clicking a node pins
 * its column so the user can assemble one path; pinned selection highlights the
 * path and dims the rest. Renders nothing without flows.
 *
 * @param {object} props
 * @param {Array<{name: string, column: string, rawKey: string, orgs: number}>} props.nodes
 * @param {Array<{source: number, target: number, value: number}>} props.links
 * @param {string[]} props.columns - ordered column keys (left → right)
 * @param {Object<string, string>} [props.selected] - column → chosen rawKey
 * @param {(column: string, rawKey: string) => void} [props.onNodeClick] - called when a node is clicked
 */
const SectorTocPathsSankey = ({ nodes = [], links = [], columns = [], selected = {}, onNodeClick }) => {
    if (!Array.isArray(nodes) || nodes.length === 0 || !Array.isArray(links) || links.length === 0) {
        return null
    }

    const firstColumn = columns[0]
    const lastColumn = columns[columns.length - 1]

    return (
        <ResponsiveContainer width='100%' height='100%'>
            <Sankey
                data={{ nodes, links }}
                node={<PathNode firstColumn={firstColumn} lastColumn={lastColumn} selected={selected} onNodeClick={onNodeClick} />}
                link={<PathLink selected={selected} />}
                nodeWidth={12}
                nodePadding={18}
                margin={{ top: 24, right: 150, bottom: 12, left: 150 }}
            >
                <Tooltip
                    formatter={(value, _name, entry) => {
                        const label = entry?.payload?.payload?.name
                        return label
                            ? [`${value} organisatie(s)`, label]
                            : [`${value} organisatie(s)`, '']
                    }}
                />
            </Sankey>
        </ResponsiveContainer>
    )
}

export default SectorTocPathsSankey
